/**
 * Premium Upgrade Button Component
 * Handles Stripe checkout flow for premium subscription
 */
import React, { useState } from 'react';
import { Crown, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PremiumUpgradeButton = ({ 
  variant = 'default', // default, compact, hero
  className = '',
  onUpgradeStart,
  onUpgradeError
}) => {
  const [loading, setLoading] = useState(false);
  
  const handleUpgrade = async () => {
    setLoading(true);
    onUpgradeStart?.();
    
    try {
      // Get auth token
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      if (!token) {
        toast.error('Please log in to upgrade');
        setLoading(false);
        return;
      }
      
      // Create checkout session
      const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(error.message || 'Failed to start upgrade process');
      onUpgradeError?.(error);
      setLoading(false);
    }
  };
  
  // Hero variant - Large, prominent button for landing/upgrade pages
  if (variant === 'hero') {
    return (
      <Button
        onClick={handleUpgrade}
        disabled={loading}
        className={`
          relative overflow-hidden group
          bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 
          hover:from-amber-600 hover:via-orange-600 hover:to-amber-600
          text-white font-bold py-6 px-8 rounded-2xl text-lg
          shadow-xl shadow-amber-500/30 hover:shadow-amber-500/40
          transition-all duration-300 hover:scale-[1.02]
          ${className}
        `}
        data-testid="premium-upgrade-hero"
      >
        {/* Animated shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        
        {loading ? (
          <>
            <Loader2 className="w-6 h-6 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <Crown className="w-6 h-6 mr-2" />
            Upgrade to Premium
            <Sparkles className="w-5 h-5 ml-2 animate-pulse" />
          </>
        )}
      </Button>
    );
  }
  
  // Compact variant - Smaller button for nav/header
  if (variant === 'compact') {
    return (
      <Button
        onClick={handleUpgrade}
        disabled={loading}
        size="sm"
        className={`
          bg-gradient-to-r from-amber-500 to-orange-500 
          hover:from-amber-600 hover:to-orange-600
          text-white font-semibold rounded-lg
          shadow-lg shadow-amber-500/20
          ${className}
        `}
        data-testid="premium-upgrade-compact"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <Crown className="w-4 h-4 mr-1" />
            Premium
          </>
        )}
      </Button>
    );
  }
  
  // Default variant
  return (
    <Button
      onClick={handleUpgrade}
      disabled={loading}
      className={`
        bg-gradient-to-r from-amber-500 to-orange-500 
        hover:from-amber-600 hover:to-orange-600
        text-white font-semibold py-5 px-6 rounded-xl
        shadow-lg shadow-amber-500/20
        transition-all duration-200 hover:scale-[1.01]
        ${className}
      `}
      data-testid="premium-upgrade-button"
    >
      {loading ? (
        <>
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          <Crown className="w-5 h-5 mr-2" />
          Go Premium
        </>
      )}
    </Button>
  );
};

export default PremiumUpgradeButton;
